import CRMStatusChip from '@components/Common/CRMStatusChip';
import { DELETED_MODES, LISTING_STATES, REQUIREMENT_STATUSES, requirementOf } from '@constants/applicationStatus';
import CRMPageTemplate from '@components/Common/CRMPageTemplate';
import ErrorMessage from '@components/Common/ErrorMessage';
import Popup from '@components/Common/Popup';
import type { Column } from '@components/Common/SearchFilter/SearchFilter';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import SelectFormField from '@components/Common/SelectFormField';
import MintlyTable from '@components/Common/Table';
import type { ColumnsConfig } from '@components/Common/Table/cardFields';
import { companyNameSearch, jobList } from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import './index.scss';
import { graphQLFilterFormatter } from '@helpers/utils';
import axios from 'axios';
import WorkIcon from '@mui/icons-material/Work';
import { Box, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import RelevancyViewPopup from './RelevancyViewPopup';
import { formatDate } from '@helpers/date';
import { useCanWriteJobsAndCompanies } from '@components/Common/Utils/roles';

/* The table's columns, named once so the loading skeleton can draw the same number of
   them. A skeleton with its own column count is a skeleton that stops matching. */
const JOB_COLUMNS = ['jobName', 'datePosted', 'company', 'recruiter', 'isExternalJob', 'jobStatus', 'location', 'createdAt'];

/* Status is sixth in the desktop order, so the tablet default (the first three) cut it at 900px:
   the one column a recruiter scans this list for. Named here so tablet keeps it. */
const JOB_COLUMN_LAYOUT: ColumnsConfig = {
  jobName: { card: 'title', tablet: true },
  company: { card: 'subtitle', tablet: true },
  jobStatus: { card: 'status', tablet: true },
};

const rowsPerPage = 100;

interface JobData {
  id: string;
  jobName: string;
  datePosted: string;
  location: string;
  locality: string;
  createdAt?: string;
  relevantCandidates: {
    jobSeekerId: string;
    jobSeekerName: string;
    percentage: number;
  }[];
  company: string;
  isExternalJob: boolean;
  jobStatus: string;
  /**
   * Who owns this job. Stored as the raw id and resolved to a name at render time.
   *
   * The list used to hide MANAGED jobs belonging to another recruiter entirely, so the only way
   * to learn a job was already taken was to be handed it. Two recruiters working the same client
   * is exactly what that was meant to prevent, and hiding the evidence made it likelier.
   *
   * Not resolved during the row mapping on purpose: the name lookup is a separate request that
   * usually lands after the jobs do, and baking the name in would freeze whatever the map held
   * at that moment, which is nothing.
   */
  assignedHrId: string;
  hiringMode: string;
}

const Jobs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(() => {
    const p = parseInt(searchParams.get('page') ?? '0', 10);
    return Number.isFinite(p) && p >= 0 ? p : 0;
  });
  const [data, setData] = useState<JobData[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  /*
   * Recruiter ids resolved to names, fetched once.
   *
   * The job rows carry `assignedHrId` and nothing else, and a column of Mongo ids tells a
   * recruiter nothing about whether the job is their colleague's. Same source the HR page
   * already uses, so the two agree about who exists.
   */
  const [hrNames, setHrNames] = useState<Record<string, string>>({});
  /*
   * The subset of the above that can actually own a job, for the Recruiter filter's dropdown.
   * Only role HR is assignable -- a job belonging to a Super Admin or a Marketing user is not
   * a thing -- but `hrNames` above stays unfiltered on purpose, because it also has to resolve
   * jobs assigned to someone who has since changed role or left.
   */
  const [hrUsers, setHrUsers] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterData, setFilterData] = useState<Column[]>(() => {
    const initial: Column[] = [];
    searchParams.forEach((value, key) => {
      if (key.startsWith('filter_') && value.trim() !== '') {
        const col = key.replace('filter_', '');
        initial.push({ id: initial.length + 1, columnName: col, searchValue: decodeURIComponent(value) });
      }
    });
    return initial;
  });
  /*
   * Staged-then-Apply, like every other control in the panel: `pending` is what the dropdown
   * shows, `selected` is what the query uses. Seeded from a bare `recruiter` param rather than
   * `filter_Recruiter` because SearchFilter deletes every `filter_*` key on each apply, so a
   * prefixed one would be wiped by the next unrelated filter change.
   */
  const [pendingRecruiter, setPendingRecruiter] = useState(() => searchParams.get('recruiter') ?? '');
  const [selectedRecruiter, setSelectedRecruiter] = useState(() => searchParams.get('recruiter') ?? '');
  const [dateFrom, setDateFrom] = useState(() => searchParams.get('date_from') ?? '');
  const [dateTo, setDateTo] = useState(() => searchParams.get('date_to') ?? '');
  const [selectedRelevantCandidates] = useState<
    { jobSeekerId: string; jobSeekerName: string; percentage: number; createdAt: string }[]
  >([]);
  const [relevancyPopupOpen, setRelevancyPopupOpen] = useState(false);

  const navigate = useNavigate();
  // Marketing sees every job but adds none (owner's brief, 2026-10-19).
  const canWriteJobs = useCanWriteJobsAndCompanies();

  // Company is a high-cardinality filter — search the server (bounded) instead of preloading
  // every company name and rendering chips, which does not scale in production.
  const searchCompanies = useCallback(async (query: string): Promise<string[]> => {
    if (!query.trim()) return [];
    const { data } = await fetchGraphQLApiData(companyNameSearch(query.trim(), 20));
    return [
      ...new Set(
        ((data?.getAllCompanies?.content ?? []) as any[])
          .map((c) => c.companyName as string)
          .filter(Boolean),
      ),
    ];
  }, []);

  const jobColMapping = {
    Company: 'company.companyName',
    /*
     * Keyed on the id, not the name, so "View this company's jobs" cannot list the wrong company.
     * Two companies sharing a name, or a rename between the link being built and followed, and a
     * name-keyed filter silently answers about somebody else. It is also what makes that link
     * work at all: it used to pass a column absent from this map, and graphQLFilterFormatter
     * turns an unmapped column into `{ key: "undefined" }` rather than failing.
     */
    'Company ID': 'companyId',
    Title: 'title',
    Locality: 'locality',
    City: 'city',
    State: 'state',
    Country: 'country',
    'External Job': 'isExternalJob',
    // What people set; the listing flag (jobStatus) is derived from it.
    Status: 'requirementStatus',
    /*
     * The listing flag, and the only way to see an expired job in this console.
     *
     * getAllJobs excludes jobStatus EXPIRED unless the request carries a jobStatus filter
     * (JobsAggregateService:264-285, covered by JobsAggregateServiceTest:405-448). Nothing here
     * produced that key, so the exclusion always applied and an expired job was unreachable from
     * the Control Center however it was filtered. The backend has supported this all along.
     *
     * Expect few rows: only 10 of 3,997 open jobs carry an expiry date at all, because until
     * recently only the employer path set one. That is the data, not a broken filter.
     */
    Listing: 'jobStatus',
    /*
     * Deleted jobs, which until now existed in the database and nowhere a person could look.
     *
     * A job removed from the employer app is soft-deleted: deleteJobById stamps deletedAt and
     * deletedBy and writes an audit row, and restoreJob undoes it. But getAllJobs excluded them
     * unconditionally, so the record was kept and was invisible -- which is indistinguishable from
     * having lost it.
     *
     * `only` lists just them, `any` lists both, and leaving it alone keeps the default. The
     * backend strips this filter for any caller that is not internal, because getAllJobs is
     * reachable anonymously by the public site.
     */
    Deleted: 'deleted',
  };

  const filterColumns = Object.keys(jobColMapping);
  const skipResetOnFirstFilterChange = useRef(true);
  /*
   * The recruiter this page last put in the URL itself.
   *
   * Applying writes the URL twice in one batch: SearchFilter rewrites the filter_* keys, then the
   * effect below adds `recruiter`. The hydration effect runs between the two and would otherwise
   * read a URL that has lost `page` but not yet gained `recruiter`, conclude the recruiter was
   * cleared, and undo the selection just made -- refetching the whole unfiltered list on the way.
   *
   * This was a boolean "a write of mine is pending", which is a question about timing, and it
   * answered wrongly often enough to matter: left raised, hydration ignored a genuine Back and
   * the list stayed filtered against a URL that said otherwise -- no error, just stale rows.
   * Comparing values instead has no such window. A URL recruiter that differs from what we wrote
   * came from somewhere else (Back, Forward, a pasted link) and is always adopted.
   */
  const lastWrittenRecruiter = useRef<string>(searchParams.get('recruiter') ?? '');

  // Commit happens before the first-change guard below, not after: that guard returns early on
  // the first apply of the session, which would swallow the recruiter selection made with it.
  // The panel cannot reset page-owned state itself, so Clear All arrives here as `clearAll`.
  const onApplyFilters = useCallback((_filters: Column[], clearAll?: boolean) => {
    if (clearAll) {
      setPendingRecruiter('');
      setSelectedRecruiter('');
    } else {
      setSelectedRecruiter(pendingRecruiter);
    }
    if (skipResetOnFirstFilterChange.current) {
      skipResetOnFirstFilterChange.current = false;
      return;
    }
    if (page !== 0) setPage(0);
  }, [page, pendingRecruiter]);

  const handleChangePage = (
    _event: React.MouseEvent<Element, MouseEvent> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    const newSearch = new URLSearchParams(searchParams);
    newSearch.set('page', String(newPage));
    if (newSearch.toString() !== searchParams.toString()) setSearchParams(newSearch);
  };

  useEffect(() => {
    let cancelled = false;
    axios
      .get('/api/users?page=0&size=200')
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.content ?? res?.data ?? [];
        const map: Record<string, string> = {};
        for (const u of list) {
          map[u._id] =
            u?.personalDetails?.fullName ||
            [u?.personalDetails?.firstName, u?.personalDetails?.lastName]
              .filter(Boolean)
              .join(' ') ||
            u._id;
        }
        setHrNames(map);
        setHrUsers(
          list
            .filter((u: any) => u?.role === 'HR')
            .map((u: any) => ({ id: u._id, name: map[u._id] }))
            .sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name)),
        );
      })
      // A failed lookup degrades to ids in the column rather than blanking the whole page: the
      // jobs are the point here, the names are the annotation.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  /*
   * Always includes whatever is selected, even before the names arrive.
   *
   * A deep link names a recruiter on the first render, while `/api/users` is still in flight, so
   * the control held a value matching no option: MUI logged an out-of-range warning and rendered
   * the field blank, which reads as the filter having been dropped. The id stands in until the
   * name lands, so the control always shows what the list is actually filtered by.
   */
  const recruiterOptions = useMemo(() => {
    const opts = [
      { value: '', label: 'Any recruiter' },
      ...hrUsers.map((u) => ({ value: u.id, label: u.name })),
    ];
    if (pendingRecruiter && !opts.some((o) => o.value === pendingRecruiter)) {
      opts.push({ value: pendingRecruiter, label: hrNames[pendingRecruiter] ?? pendingRecruiter });
    }
    return opts;
  }, [hrUsers, hrNames, pendingRecruiter]);

  /*
   * Responses can land out of order: a filter change fires a new request while the previous one
   * is still in flight, and if the older one answers last it overwrites the newer list. Each call
   * takes a number and only the latest one may write state.
   */
  const fetchSeq = useRef(0);

  const fetchData = useCallback(
    async (page: number) => {
      const seq = ++fetchSeq.current;
      setLoading(true);
      setError(null);
      const filter = graphQLFilterFormatter(filterData, jobColMapping, ['External Job']);
      if (dateFrom) filter.push(`{ key: "createdAtFrom", value: "${dateFrom}" }`);
      if (dateTo) filter.push(`{ key: "createdAtTo", value: "${dateTo}" }`);
      /* No companion hiringMode: "MANAGED" filter. Distinct keys AND together server-side, so
         it would be redundant for well-formed data while hiding a SELF_SERVE job that wrongly
         carries an owner -- which is exactly the row someone filtering by recruiter wants. */
      if (selectedRecruiter) filter.push(`{ key: "assignedHrId", value: "${selectedRecruiter}" }`);
      try {
        const query = jobList(page, rowsPerPage, filter);
        const { data } = await fetchGraphQLApiData(query);
        if (seq !== fetchSeq.current) return;
        const { getAllJobs } = data;
        if (getAllJobs.content && getAllJobs.totalElements !== undefined) {
          const jobs: JobData[] = getAllJobs.content.map((item: any) => ({
            id: item._id,
            isExternalJob: !!item.isExternalJob,
            company: item.isExternalJob
              ? item.companyName ?? '-'
              : item.company?.companyName ?? '-',
            relevantCandidates: item.relevantCandidates ?? [],
            datePosted: item.postedDate
              ? formatDate(item.postedDate)
              : '-',
            jobName: item.title,
            locality: item.locality ?? '',
            location: [item.locality, item.city, item.state, item.country]
              .filter(Boolean)
              .join(', ') || item.location || '',
            createdAt: item.createdAt || '',
            jobStatus: requirementOf(item.requirementStatus, item.jobStatus),
            /*
             * Kept raw beside the requirement label. Expiry sets requirementStatus to Closed AND
             * jobStatus to EXPIRED, so the column showed "Closed" and an expired job was
             * indistinguishable from one somebody closed on purpose.
             */
            listing: (item.jobStatus ?? '').toUpperCase(),
            deletedAt: item.deletedAt || '',
            deletedBy: item.deletedBy || '',
            assignedHrId: item.assignedHrId ?? '',
            hiringMode: item.hiringMode ?? '',
          }));
          setData(jobs);
          setTotalElements(getAllJobs.totalElements);
        } else {
          throw new Error('Unexpected response format');
        }
      } catch (error) {
        if (seq !== fetchSeq.current) return;
        console.error('Error fetching jobs:', error);
        setError('Failed to fetch data. Please try again later.');
      } finally {
        if (seq === fetchSeq.current) setLoading(false);
      }
    },
    [filterData, dateFrom, dateTo, selectedRecruiter],
  );

  useEffect(() => { fetchData(page); }, [fetchData, page, filterData]);

  useEffect(() => {
    const p = parseInt(searchParams.get('page') ?? '0', 10);
    const safe = Number.isFinite(p) && p >= 0 ? p : 0;
    if (safe !== page) setPage(safe);
    setDateFrom(searchParams.get('date_from') ?? '');
    setDateTo(searchParams.get('date_to') ?? '');
    // Adopt only what this page did not write. Equal means the URL already agrees with us, so
    // there is nothing to adopt; different means the change came from outside.
    const urlRecruiter = searchParams.get('recruiter') ?? '';
    if (urlRecruiter !== lastWrittenRecruiter.current) {
      lastWrittenRecruiter.current = urlRecruiter;
      setPendingRecruiter(urlRecruiter);
      setSelectedRecruiter(urlRecruiter);
    }
  }, [searchParams.toString()]);

  // The params the filter panel does not own: dates and the recruiter. Kept in one effect so
  // there is a single writer for them and two of them cannot race each other's URLSearchParams.
  const skipParamUrlSyncOnMount = useRef(true);
  useEffect(() => {
    if (skipParamUrlSyncOnMount.current) {
      skipParamUrlSyncOnMount.current = false;
      return;
    }
    /*
     * Built from the live URL rather than from the `prev` react-router hands us.
     *
     * Applying writes twice in one batch: SearchFilter sets the filter_* keys, then this effect
     * adds the params it owns. The `prev` this effect is given still predates SearchFilter's
     * write, so rebuilding from it put back a query string that never had the column filters in
     * it -- typing a Title, picking a recruiter and pressing Apply silently dropped the Title.
     *
     * The date range has always had this too, and it is reproducible with no recruiter in play:
     * set a Title and a date range together and only the dates survive. `window.location.search`
     * is updated synchronously by the history push, so by the time an effect runs it is the one
     * description of the URL that is never behind.
     */
    const n = new URLSearchParams(window.location.search);
    if (dateFrom) n.set('date_from', dateFrom); else n.delete('date_from');
    if (dateTo) n.set('date_to', dateTo); else n.delete('date_to');
    if (selectedRecruiter) n.set('recruiter', selectedRecruiter); else n.delete('recruiter');
    if (n.toString() !== window.location.search.replace(/^\?/, '')) setSearchParams(n);
    lastWrittenRecruiter.current = selectedRecruiter;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, selectedRecruiter]);

  // Opening a job means routing to its workspace. The list used to also render
  // the old modal inline, so one click produced both.
  const handleOpenPopup = (jobId: string) => {
    navigate(`/jobs/${jobId}`);
  };

  return (
    <>
      <CRMPageTemplate
        addNewLabel="Add Job"
        booleanColumns={['External Job']}
        columnOptions={{ Status: [...REQUIREMENT_STATUSES], Listing: [...LISTING_STATES], Deleted: [...DELETED_MODES] }}
        asyncSearch={{ Company: searchCompanies }}
        filterColumns={filterColumns}
        locationColumns={['City', 'State', 'Country']}
        localityColumns={['Locality']}
        localityAutoFillColumns={{ Locality: { city: 'City', state: 'State', country: 'Country' } }}
        filterLabel="Filter"
        icon={<WorkIcon />}
        initialColumns={filterData}
        setFilterData={setFilterData}
        title="Jobs"
        totalCount={totalElements}
        onAddNew={canWriteJobs ? () => navigate('/jobs/new') : undefined}
        onApplyFilters={onApplyFilters}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={v => setDateFrom(v)}
        onDateToChange={v => setDateTo(v)}
        extraFilterContent={
          /* Not a filterColumn: graphQLFilterFormatter sends the displayed text as the value,
             and this one shows a name while sending a users._id. Same .sf-drawer-field wrapper
             as the native fields so it does not read as a bolted-on control. */
          <Box className="sf-drawer-field">
            <Typography className="sf-drawer-field-label" variant="overline">
              Recruiter
            </Typography>
            {/* No sizing here: .sf-drawer-field in SearchFilter.scss gives every control in the
                panel its 36px, this one included. */}
            <SelectFormField
              required={false}
              fullWidth
              size="small"
              name="recruiterFilter"
              label=""
              options={recruiterOptions}
              value={pendingRecruiter}
              onChange={(_n, v) => setPendingRecruiter(String(v))}
            />
          </Box>
        }
      >
        {loading ? (
          <SkeletonLoader rows={20} columns={JOB_COLUMNS.length} statusChip />
        ) : error ? (
          <ErrorMessage message={error} />
        ) : data.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, py: 8 }}>
            <Typography color="text.secondary" variant="body2">No jobs found.</Typography>
          </Box>
        ) : (
          <MintlyTable
            pagination={{
              enabled: true,
              page,
              rowsPerPage,
              totalNumberOfRows: totalElements,
              handleChangePage,
            }}
            rows={data}
            selectors={JOB_COLUMNS}
            columns={JOB_COLUMN_LAYOUT}
            getRowKey={(row: any, i: number) => row.id ?? i}
            tableBodyRow={{
              jobName: (row: any) => <>{row.jobName}</>,
              datePosted: (row: any) => <>{row.datePosted}</>,
              company: (row: any) => <>{row.company}</>,
              /* Only MANAGED jobs have an owner: a self-serve job is the employer's own, so
                 "Unassigned" there would imply a gap that is not one. An id with no name behind
                 it still shows the id, which is worth more than a blank when someone is trying
                 to find out who to ask. */
              recruiter: (row: any) =>
                row.hiringMode !== 'MANAGED' ? (
                  <>-</>
                ) : row.assignedHrId ? (
                  <>{hrNames[row.assignedHrId] ?? row.assignedHrId}</>
                ) : (
                  <>Unassigned</>
                ),
              isExternalJob: (row: any) => <>{row.isExternalJob ? 'Yes' : 'No'}</>,
              // Text, not a chip: a chip makes the row 3px taller than the list skeleton it replaces.
              jobStatus: (row: any) =>
                /*
                 * Deleted outranks every other state in the column. A row that says "Active" while
                 * the job has been deleted is the most misleading thing this list could show, and
                 * the chip is the only place a reader learns it at all.
                 */
                row.deletedAt ? (
                  <CRMStatusChip status="archived" label="Deleted" />
                ) : row.listing === 'EXPIRED' ? (
                  // CRMStatusChip has carried an amber `expired` variant that nothing ever fed.
                  <CRMStatusChip status="expired" label="Expired" />
                ) : (
                  <CRMStatusChip status={row.jobStatus || 'Active'} />
                ),
              location: (row: any) => <>{row.location || '-'}</>,
              createdAt: (row: any) => <>{formatDate(row.createdAt)}</>,
            }}
            tableHeadRow={{
              jobName: <>Title</>,
              datePosted: <>Date Posted</>,
              company: <>Company</>,
              recruiter: <>Recruiter</>,
              isExternalJob: <>External Job</>,
              jobStatus: <>Status</>,
              location: <>Location</>,
              createdAt: <>Created</>,
            }}
            onRowClick={(row) => handleOpenPopup(row.id)}
          />
        )}
      </CRMPageTemplate>

      <Popup open={relevancyPopupOpen} onClose={() => setRelevancyPopupOpen(false)}>
        <RelevancyViewPopup
          relevantCandidates={selectedRelevantCandidates}
          onClose={() => setRelevancyPopupOpen(false)}
        />
      </Popup>
    </>
  );
};

export default Jobs;
